import React from 'react'
import ReactDOM from 'react-dom/client'
 
const rootElement = document.getElementById('root')

if (!rootElement) {
  throw new Error('Root element #root was not found in index.html')
}

const root = ReactDOM.createRoot(rootElement)

const formatError = (error: unknown) => {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}${error.stack ? `\n\n${error.stack}` : ''}`
  }

  return String(error)
}

const renderFatalError = (error: unknown) => {
  const safeMessage = formatError(error)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

  rootElement.innerHTML = `
    <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;background:#0f172a;color:#e2e8f0;font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,'Liberation Mono','Courier New',monospace;">
      <div style="max-width:960px;width:100%;border:1px solid #334155;border-radius:12px;background:#111827;padding:20px;box-shadow:0 8px 30px rgba(2,6,23,.5);">
        <h1 style="margin:0 0 12px 0;font-size:18px;line-height:1.3;color:#f8fafc;">App failed to load</h1>
        <p style="margin:0 0 14px 0;color:#94a3b8;font-size:13px;">A runtime error prevented React from rendering. Details:</p>
        <pre style="margin:0;white-space:pre-wrap;word-break:break-word;background:#020617;border:1px solid #1e293b;border-radius:8px;padding:12px;font-size:12px;line-height:1.5;color:#cbd5e1;">${safeMessage}</pre>
      </div>
    </div>
  `
}

class RootErrorBoundary extends React.Component<React.PropsWithChildren, { error: Error | null }> {
  state = { error: null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error) {
    console.error('React render error:', error)
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: '100vh', padding: 24, background: '#0f172a', color: '#e2e8f0' }}>
          <h1 style={{ margin: 0, fontSize: 18, color: '#f8fafc' }}>Render failed</h1>
          <pre
            style={{
              marginTop: 12,
              whiteSpace: 'pre-wrap',
              background: '#020617',
              border: '1px solid #1e293b',
              borderRadius: 8,
              padding: 12,
              fontSize: 12,
              lineHeight: 1.5,
              color: '#cbd5e1',
            }}
          >
            {formatError(this.state.error)}
          </pre>
        </div>
      )
    }

    return this.props.children
  }
}

void import('./App')
  .then(({ default: App }) => {
    root.render(
      <React.StrictMode>
        <RootErrorBoundary>
          <App />
        </RootErrorBoundary>
      </React.StrictMode>
    )
  })
  .catch((error) => {
    console.error('Bootstrap error:', error)
    renderFatalError(error)
  })
